import {
  Button,
  Card,
  EmptyState,
  Flex,
  Modal,
  SimpleTable,
  Typography,
} from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";

const { Title, Text } = Typography;

const ModalPage = () => {
  const [modal, setModal] = useState(false);
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Modal</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Modal component provides a flexible way to display content in a
            dialog overlay. It's designed to handle interactions that require
            focus from the user, such as confirmations, forms, or additional
            details. The Modal can be customized with various props for title,
            close behavior, and additional styling.
          </Text>

          <Title level={5}>Usage</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            To use the Modal component, simply wrap the content you want inside
            the Modal and control its visibility using the isOpen prop. You can
            also provide a title, manage whether the Modal can be closed by
            clicking the close icon, and trigger actions when the Modal is fully
            rendered
          </Text>

          <Title level={5}>Behavior</Title>
          {/* <Text size="sm"></Text> */}
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Closing the Modal
                </Text>
                : The onClose prop controls the close behavior. If closable is
                true, a close icon will appear in the top-right corner of the
                modal. When clicked, it triggers the onClose function. If
                closable is false, the Modal will not show the close icon, and
                the user must handle closing the Modal via other interactions.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Rendering Logic
                </Text>
                : The rendered prop can be used to trigger a function once the
                Modal has fully appeared. This can be useful for triggering
                animations, logging events, or performing other actions after
                the Modal is rendered.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Animation
                </Text>
                : The Modal includes built-in transition animations, including
                fade-in and scale effects when the Modal opens or closes. These
                animations are triggered based on the isOpen state.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-5">
            Styling
          </Title>
          <Text size="sm">
            The Modal component uses a combination of TailwindCSS utility
            classes and cva to handle its appearance. You can further customize
            the Modal's appearance by passing custom class names through the
            contentClassName and containerClassName props
          </Text>
          <Title level={6} className="mt-3">
            Customizing with className
          </Title>
          <Text size="sm">
            You can pass additional classes to customize the look of the modal
            container or the modal content itself
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: ModalExample1,
              }}
            >
              <Flex direction={"col"} className="gap-3 w-full">
                <Button onClick={() => setModal(true)}>Click to open!</Button>
                <Modal
                  isOpen={modal}
                  onClose={() => setModal(false)}
                  title="Modal title"
                  contentClassName="max-w-md"
                >
                  <EmptyState />
                </Modal>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* title */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">title</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The title displayed at the top of the Modal.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">React.ReactNode</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">null</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* isOpen */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">isOpen</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls whether the Modal is visible or not.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">boolean</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onClose */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onClose</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Function to be called when the Modal is closed.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`() => void`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">null</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* closable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">closable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, a close icon is displayed, allowing the user to
                        close the Modal.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">boolean</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* rendered */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">rendered</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional callback function that runs after the Modal has
                        finished rendering.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">{`() => void`}</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">null</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* contentClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">contentClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes for the modal content container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">string</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">null</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* containerClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">containerClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes for the outer container of the
                        Modal.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">string</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">null</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to be displayed inside the modal.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">React.ReactNode</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const ModalExample1 = `
  <Button onClick={() => setModal(true)}>Click to open!</Button>
  
  <Modal
    isOpen={modal}
    onClose={() => setModal(false)}
    title="Modal title"
    contentClassName="max-w-md"
  >
    <EmptyState />
  </Modal>
`;
export default ModalPage;
